/**
 * Universal Sync Bridge Module Map
 * Maps legacy storage base keys to cloud collections
 * 
 * CRITICAL: This is the ONLY place where legacy → cloud translation happens
 * Modules continue using legacy storage patterns (base keys only)
 * The Sync Bridge uses this map to push/pull cloud data
 */

// FT-25 REWRITE (2026-03-19): Removed hoursOfRestAdapter import
// HOR now syncs like crew - simple string mapping, no adapter

/**
 * FT-05: Org-scoped modules Set
 * These modules store data at org level (fleet-wide), not per-vessel.
 * Used by:
 * - useStorageState to skip vessel ID in key
 * - UniversalSyncBridge to bypass vessel filter
 * - storage.js to correctly extract module name
 */
export const ORG_SCOPED_MODULES = new Set([
  'fleet_crew',              // FT-05: Fleet-wide crew roster
  'fleet_risk_assessments',  // FT-116: Fleet-wide risk assessments
  'hours_of_rest'            // FT-156: Org-scoped HOR (crew travels between vessels)
]);

export const MODULE_MAP = {
  // FT-05/FT-116: Fleet-wide modules (org-scoped, not vessel-scoped)
  fleet_crew: 'sr.fleet_crew.v1',
  fleet_risk_assessments: 'sr.fleet_risk_assessments.v1',  // FT-116: Fleet-wide risk assessments

  // Core operational modules
  drills: 'sr.drills.v1',
  drill_register: 'sr.drill_register.v1',  // FT-406 FIX: Per-vessel drill DEFINITIONS/compliance-schedule ledger (was silently dropped: absent from MODULE_MAP)
  drill_settings: 'sr.drill_settings.v1',  // FT-11 FIX: Drill frequencies, enabled flags, custom drills sync
  drills_config: 'sr.drills_config.v1',    // FT-112c: New drills config hook (companyRequiredDrills, drillFrequencies)
  onboard_training: 'sr.training.v1',
  daily_checks: 'sr.daily_checks.v1',     // Date-scoped adapter in bridge
  defects: 'sr.defects.v1',
  defects_config: 'sr.defects_config.v1',  // FT-114: Defects module settings sync

  // Maintenance & equipment
  maintenance_jobs: 'sr.maintenance_jobs.v1',       // FIXED 2026-02-16: Separate collection for job definitions
  // FT-86: Removed custom_maintenance_jobs - fully migrated to maintenance_jobs (2026-03-28)
  maintenance_schedule: 'sr.maintenance.v1',
  maintenance_history: 'sr.maintenance_history.v1',  // CRITICAL FIX 2026-02-18: Completion records - was missing from MODULE_MAP!
  equipment_register: 'sr.equipment_records.v1',  // FIXED: was equipment_records (2026-01-22)
  equipment_selections: 'sr.equipment_selections.v1',  // FT-10 FIX: User selections from wizard (sync across devices)
  equipmentChecks: 'sr.equipment_checks.v1',
  maintenance_config: 'sr.maintenance_config.v1',  // FT-04 FIX: Sync advanceWarningDays, visibleCategories across devices
  job_descriptions: 'sr.job_descriptions.v1',  // FT-137: Custom job descriptions (per job ID)

  // Crew management
  crew: 'sr.crew.v1',               // FIXED: was crewMembers (2026-01-22)
  familiarisation_records: 'sr.familiarisation.v1', // FIXED: was familiarisation_progress (2026-01-22)
  // FT-15 FIX: Missing familiarisation module mappings
  crew_familiarisations: 'sr.crew_familiarisations.v1',           // Per-crew familiarisation assignments
  familiarisation_checklist_templates: 'sr.familiarisation_templates.v1',  // Custom templates
  familiarisationSettings: 'sr.familiarisation_settings.v1',       // Module settings
  familiarisation_progress: 'sr.familiarisation_progress.v1',      // FT-19: Draft progress (in-progress familiarisations)
  // FT-25 REWRITE (2026-03-19): Simple string mapping like crew/drills
  // HOR stores { buckets: {...}, signatures: {...} } - full object synced as one cloud doc
  hours_of_rest: 'sr.hours_of_rest.v1',
  hor_signatures: 'sr.hor_signatures.v1',  // Legacy - now part of hours_of_rest

  // Voyage & operations
  log_entries: 'sr.log_entries.v1',
  voyages: 'sr.voyages.v1',
  passage_plan: 'sr.passage_plan.v1',
  // FT-88: Bare module aliases for regulation definitions (e.g., dailyChecks.js module: 'operations')
  // FT-129: Removed 'operations' alias - was duplicating 766KB+ logbook data as operations_<org>_<vessel>
  // Same pattern as FT-88 seaready_checklist_records removal. operations_main_logbook handles all logbook sync.
  // FT-324 (2026-07-06): Removed 'checklist' alias — it was the wormhole feeding the ghost cloud
  // doc pipeline. Pull-side hydration writes to `checklist_${vesselId}` LS key fired sr:storage:set
  // events; the generic underscore fallback in extractModule() returned 'checklist' as the module;
  // the sync bridge then queued pushes for module 'checklist' → cloud collection
  // sr.checklist_records.v1 at docId `${vesselId}_checklist`, alongside the real
  // `${vesselId}_checklist_records`. Result: duplicate ghost docs holding stale content,
  // merged forward on every push, silently overwriting real records.
  // extractModule() in storage.js now explicitly resolves `checklist_${vesselId}` to
  // 'checklist_records', so the alias is unnecessary AND actively harmful.
  operations_main_logbook: 'sr.operations_logbook.v1',  // ADD: operations module (2026-01-22)
  operations_voyages: 'sr.operations_voyages.v1',      // ADD: operations module (2026-01-22)
  // FIXED 2026-02-27: Operations sub-modules (were missing sync)
  operations_checklists: 'sr.operations_checklists.v1',
  operations_checklist_templates: 'sr.operations_checklist_templates.v1',
  operations_permits: 'sr.operations_permits.v1',
  operations_bunkering: 'sr.operations_bunkering.v1',
  operations_checklist_favourites: 'sr.operations_favourites.v1',
  // FT-354d (2026-07-10): navigation_favourites removed — feature stripped due to
  // FT-355 storage-layer interaction bug (noScope not honoured by toOrgScopedKey).
  operations_voyage_templates: 'sr.operations_voyage_templates.v1',  // Phase 3: Voyage templates
  // FIXED 2026-02-27: Special operations
  pilot_transfers: 'sr.pilot_transfers.v1',
  towing_operations: 'sr.towing_operations.v1',
  diving_operations: 'sr.diving_operations.v1',
  checklist_records: 'sr.checklist_records.v1',
  // FT-88: Removed seaready_checklist_records alias - was duplicating data.
  // FT-324 (2026-07-06): useChecklistRecords now writes to canonical `checklist_records_${vesselId}`
  // (was `seaready_checklist_records_${vesselId}` — see one-shot migration in the hook).
  // storage.js extractModule() keeps the seaready_checklist_records_ prefix rule as a
  // compatibility path for devices mid-migration.
  operations_daily_checks_config: 'sr.operations_daily_checks_config.v1',  // FT-80 Phase 13: Daily Checks category config sync
  checklist_configs: 'sr.checklist_configs.v1',  // FT-81: All checklist settings (Navigation, Operational, Emergency)
  seaready_periodic_inspections: 'sr.periodic_inspections.v1',  // FT-86: Periodic inspections (usePeriodicInspections hook key)

  // Safety & compliance
  incidents: 'sr.incidents.v1',
  incidents_config: 'sr.incidents_config.v1',  // FT-114: Incidents module settings sync
  risk_assessments: 'sr.risk_assessments.v1',  // FIXED 2026-02-23: key must match frontend STORAGE_KEY (underscore)
  risk_assessment_config: 'sr.risk_assessment_config.v1',  // FT-114: Risk assessment settings sync
  audits: 'sr.audits.v1',             // Days 4-5 v4.2
  audit_log: 'sr.audit_log.v1',          // Days 16-17 v4.2: Module audit trails
  compliance_items: 'sr.compliance.v1',         // Days 4-5 v4.2
  compliance_register: 'sr.compliance_register.v1',  // FT-122: Compliance Register (certs, policies, SMS docs)

  // Documents (these should migrate to pure cloud eventually)
  policies: 'sr.policies.v1',          // Legacy - kept for migration
  sms_documents: 'sr.sms_documents.v1',     // New simplified config (2026-01-25)
  reports: 'sr.reports.v1',

  // Vessel configuration (special handling)
  vesselSettings: 'sr.vessel_settings.v1',
  voyage_settings: 'sr.voyage_settings.v1',  // Phase 5: Voyage settings sync (2026-03-13)
  voyage_phases: 'sr.voyage_phases.v1',  // FT-461: per-vessel voyage phase-state OBJECT keyed by voyageId ({voyageId: {currentPhase, timestamp, history}}) — LWW object-replace path, NOT in ARRAY_COLLECTIONS
  customDrills: 'sr.custom_drills.v1',
  customFamiliarisationItems: 'sr.custom_familiarisation.v1',

  // Fleet management (2026-01-25)
  fleet_notifications: 'sr.fleet_notifications.v1',      // Shore-to-vessel notifications
  fleet_notification_acks: 'sr.fleet_notification_acks.v1',  // Acknowledgements from vessel
  shore_edit_audit: 'sr.shore_edit_audit.v1',         // Audit trail (fleet-wide, not vessel-scoped)
  user_vessel_access: 'sr.user_vessel_access.v1',       // Per-user vessel access levels

  // FT-344 (2026-07-08): Photo evidence sync collection.
  // Photo records live in Dexie `photos` + `photoThumbs` tables. Push-side:
  // `usePhotoStore.savePhoto()` enqueues directly to the outbox via
  // `pushOutbox({ collection: 'sr.photos.v1', ... })` — the tables live outside
  // `db.storage`, so writes DON'T fire `sr:storage:set` events (no dispatch,
  // no ambient sync-bridge push). Pull-side: `photos` bypasses the generic
  // ARRAY_COLLECTIONS `storageRouter.writeJSON` in `UniversalSyncBridge.handleCloudUpdate`
  // via an explicit `collection === 'sr.photos.v1'` special-case (see
  // UniversalSyncBridge.js). The bridge dispatches `sr:sync:hydrate` and RETURNS;
  // `photoPullMerge.installPhotoPullListener` catches the event, decodes base64 →
  // Blob, and writes to the correct IDB tables. See DESIGN-BRIEF §3.
  photos: 'sr.photos.v1'
};

/**
 * Collection schemas for cloud storage
 * Minimal shapes optimized for sync
 */
export const COLLECTION_SCHEMAS = {
  'sr.drills.v1': {
    id: String,
    vesselId: String,
    type: String,
    date: String,
    outcome: String,
    participants: Array,
    notes: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.daily_checks.v1': {
    id: String,
    vesselId: String,
    date: String,  // YYYY-MM-DD format
    items: Array,
    completedBy: String,
    notes: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.hours_of_rest.v1': {
    id: String,
    vesselId: String,
    crewId: String,
    month: String,  // YYYY-MM format
    records: Object, // Day -> work/rest hours
    signatures: Object,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.defects.v1': {
    id: String,
    vesselId: String,
    title: String,
    status: String,
    priority: String,
    location: String,
    description: String,
    reportedBy: String,
    reportedDate: String,
    resolvedDate: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.maintenance_jobs.v1': {
    id: String,
    vesselId: String,
    itemName: String,
    equipmentType: String,
    interval: Number,
    intervalUnit: String,
    linkedEquipmentIds: Array,
    completions: Array,  // Array of completion records
    status: String,      // FT-182: 'archived' for deleted jobs (tombstone pattern)
    archivedAt: Number,  // FT-182: Timestamp when archived
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.audit_log.v1': {
    id: String,
    vesselId: String,
    module: String,               // 'equipment', 'drills', 'crew', etc.
    action: String,               // 'create', 'update', 'delete', 'correction', 'restore'
    timestamp: String,            // ISO 8601
    user: Object,                 // { id, name, role }
    item: Object,                 // { id, type, name, category, location, serialNumber }
    previousHash: String,         // For chain verification (null for genesis)
    hash: String,                 // SHA-256 of entry
    reason: Object,               // { code, label, notes }
    snapshot: Object,             // Full item data for restoration
    moduleSpecificData: Object,   // Flexible per-module fields
    retentionUntil: String,       // ISO date for retention policy
    authorizedBy: String,         // For corrections and sensitive actions
    correctsEntryId: String,      // For correction entries
    originalSnapshot: Object,     // For correction entries
    correctedSnapshot: Object,    // For correction entries
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.log_entries.v1': {
    id: String,
    vesselId: String,
    timestamp: Number,
    entryType: String,
    text: String,
    author: String,
    attachments: Array,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.voyages.v1': {
    id: String,
    vesselId: String,
    departure: Object,
    arrival: Object,
    plan: Object,
    status: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.crew.v1': {
    id: String,
    vesselId: String,
    name: String,
    position: String,
    certificates: Array,
    status: String,
    joinedDate: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  // Fleet Management Schemas (2026-01-25)
  'sr.fleet_notifications.v1': {
    id: String,
    vesselId: String,
    type: String,           // 'shore_edit', 'request', 'alert'
    priority: String,       // 'critical', 'high', 'medium', 'low'
    title: String,
    message: String,
    change: Object,         // { module, field, oldValue, newValue, reason }
    editedBy: Object,       // { userId, name, role }
    editedAt: String,       // ISO timestamp - when change was made
    deliveredAt: String,    // ISO timestamp - when vessel synced
    acknowledgedAt: String, // ISO timestamp - when Master acknowledged
    acknowledgedBy: Object,
    acknowledgementType: String, // 'accepted', 'queried'
    query: Object,          // { queriedAt, queryText, respondedAt, responseText }
    escalations: Array,     // [ { level, triggeredAt, action } ]
    entrySource: String,
    entryMethod: String,
    syncStatus: Object,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.fleet_notification_acks.v1': {
    id: String,
    notificationId: String,
    vesselId: String,
    acknowledgedBy: String,
    acknowledgedByName: String,
    acknowledgedAt: String,
    action: String,
    notes: String,
    syncStatus: Object,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.shore_edit_audit.v1': {
    id: String,
    action: String,         // 'SHORE_EDIT'
    module: String,
    field: String,
    itemId: String,
    itemName: String,
    oldValue: String,
    newValue: String,
    editReason: String,
    editedBy: Object,
    entrySource: String,
    entryMethod: String,
    editedAt: String,
    notificationId: String,
    vesselId: String,
    vesselNotified: Boolean,
    vesselDeliveredAt: String,
    vesselAcknowledged: Boolean,
    vesselAcknowledgedAt: String,
    vesselAcknowledgedBy: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  },

  'sr.user_vessel_access.v1': {
    id: String,
    userId: String,
    vesselId: String,
    accessLevel: String,    // 'view_only', 'full'
    grantedBy: String,
    grantedAt: String,
    revokedAt: String,
    createdAt: Number,
    updatedAt: Number,
    syncedAt: Number
  }
};

/**
 * Array-based collections
 * Collections that store data as arrays of items (not single objects)
 * Used by UniversalSyncBridge to aggregate pull results correctly
 */
export const ARRAY_COLLECTIONS = new Set([
  'sr.fleet_crew.v1',              // FT-05: Fleet-wide crew roster (org-scoped)
  'sr.fleet_risk_assessments.v1',  // FT-116: Fleet-wide risk assessments (org-scoped)
  'sr.crew.v1',
  'sr.drills.v1',
  'sr.drill_register.v1',          // FT-406: Per-vessel drill register ledger (flat array), peer of sr.drills.v1
  'sr.defects.v1',
  'sr.equipment_records.v1',
  'sr.incidents.v1',
  'sr.maintenance.v1',
  'sr.maintenance_jobs.v1',
  'sr.maintenance_history.v1',  // CRITICAL FIX 2026-02-18: Completion records
  // FT-86: Removed custom_maintenance_jobs - fully migrated to maintenance_jobs (2026-03-28)
  'sr.training.v1',
  'sr.familiarisation.v1',
  'sr.crew_familiarisations.v1',           // FT-15 FIX
  // FT-86 FIX: REMOVED sr.familiarisation_templates.v1 from ARRAY_COLLECTIONS
  // Data is {version, lastUpdated, templates: []} — an OBJECT with nested array, not flat array
  // Same bug pattern as FT-58 (sms_documents) — empty nested array was blocking valid pushes
  // FT-115 FIX: REMOVED sr.familiarisation_settings.v1 from ARRAY_COLLECTIONS
  // Data is { validityPeriodMonths: 12 } — an OBJECT, not an array
  // Backend was rejecting the push with validation error because it expected array format
  // Note: familiarisationSettings is now consolidated into familiarisation_checklist_templates
  'sr.familiarisation_progress.v1',        // FT-19: Draft progress
  // FT-164 (2026-04-14): RE-ADDED sr.hours_of_rest.v1 to ARRAY_COLLECTIONS
  // Converting HOR from nested bucket format to FLAT ARRAY like fleet_crew
  // This fixes the unreliable object push path sync issues
  'sr.hours_of_rest.v1',
  'sr.risk_assessments.v1',
  'sr.audits.v1',
  'sr.compliance.v1',
  'sr.policies.v1',
  // FT-58 FIX (2026-03-24): REMOVED sr.sms_documents.v1 from ARRAY_COLLECTIONS
  // sms_documents is a config OBJECT, NOT an array - same bug pattern as FT-25 (hours_of_rest)
  // Having it here caused infinite sync loop: empty array detection → blocked push → force-pull → repeat
  'sr.reports.v1',
  'sr.log_entries.v1',
  'sr.voyages.v1',
  'sr.equipment_checks.v1',
  'sr.audit_log.v1',
  'sr.fleet_notifications.v1',
  'sr.fleet_notification_acks.v1',
  'sr.shore_edit_audit.v1',
  'sr.user_vessel_access.v1',
  'sr.operations_logbook.v1',
  'sr.operations_voyages.v1',
  'sr.passage_plan.v1',
  'sr.custom_drills.v1',
  'sr.custom_familiarisation.v1',
  // FIXED 2026-02-27: Operations sub-modules
  'sr.operations_checklists.v1',
  'sr.operations_checklist_templates.v1',
  'sr.operations_permits.v1',
  'sr.operations_bunkering.v1',
  'sr.operations_favourites.v1',
  // FT-354d (2026-07-10): 'sr.navigation_favourites.v1' removed with the favourites feature.
  'sr.operations_voyage_templates.v1',  // Phase 3: Voyage templates
  'sr.pilot_transfers.v1',
  'sr.towing_operations.v1',
  'sr.diving_operations.v1',
  'sr.checklist_records.v1',
  'sr.compliance_register.v1',  // FT-122: Compliance Register
  'sr.photos.v1'  // FT-344: Photo evidence — array of photo records (see DESIGN-BRIEF §3)
]);

/**
 * Date-scoped adapters
 * Daily checks now store dates inside the value object, not in the key
 * The base key 'daily_checks' contains an object with dates as keys
 */
export const DATE_SCOPED_MODULES = {
  daily_checks: {
    // Extract date from storage key (e.g., 'daily_checks_vessel123' returns today's date context)
    // For date-scoped modules, the date is stored INSIDE the value, not in the key
    extractDate: (storageKey) => {
      // The key is 'daily_checks_{vesselId}', date is inside the value
      // Return null to indicate we need to check the value for dates
      return null;
    },
    // Create cloud document ID from vesselId and date
    createCloudId: (vesselId, date) => {
      return `${vesselId}_${date}`;
    },
    // Create legacy storage key (for compatibility)
    createLegacyKey: (date) => {
      // Daily checks uses a single key with dates nested inside
      // This is called per-date when syncing individual dates
      return `daily_checks`;
    },
    // Transform from local nested structure to cloud documents
    toCloudDocuments: (localData, vesselId) => {
      // localData is { '2025-08-30': {...}, '2025-08-31': {...} }
      return Object.entries(localData || {}).map(([date, checks]) => ({
        id: `${vesselId}_${date}`,
        vesselId,
        date,
        items: checks,
        createdAt: Date.now(),
        updatedAt: Date.now()
      }));
    },
    // Transform from cloud documents to local nested structure
    fromCloudDocuments: (cloudDocs) => {
      // Convert array of documents to nested object
      const result = {};
      cloudDocs.forEach(doc => {
        if (doc.date) {
          result[doc.date] = doc.items || {};
        }
      });
      return result;
    }
  }
};

/**
 * Conflict resolution policy
 */
export const SYNC_POLICY = {
  conflictResolution: 'LAST_WRITE_WINS',  // Server timestamp determines winner
  syncInterval: 300000,  // 5 minutes (background pull only - push on change is instant!)
  batchSize: 100,  // Max documents per sync batch
  retryAttempts: 3,
  retryDelay: 1000,  // 1 second base delay (exponential backoff)
};

export default MODULE_MAP;